// 機房（plan SPEC #kinio-station-seat-site-share，Eddy 2026-10-20 選單）：/siteroom 依場域列機房管理站，/siteroom/:aid 單站
// 的總覽、路由器、伺服器、NAS。只有查詢、沒有任何改設定的按鈕；開分頁時跑一次，使用者按〔重新整理〕才再跑（不自動輪詢）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Route, Routes, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isKinioError } from '@kinio/web-sdk';
import { TAB_RESOURCE, availableTabs, cellText, resultBlocks, runAndWait, type SiteRun } from '../../lib/siteRoom';
import { useSiteRoom, type SiteRoomStation } from './useSiteRoom';

function errText(e: unknown): string {
  if (isKinioError(e)) {
    const dc = e.detail.code;
    return e.code === 'DEVICE_ERROR' && typeof dc === 'string' && dc ? dc : e.code;
  }
  return e instanceof Error ? e.message : 'ERROR';
}

export function SiteRoomPage() {
  const { t } = useTranslation('siteroom');
  const { stations, loading } = useSiteRoom();
  if (stations.length === 0) {
    return <p className="card muted" data-testid="siteroom-empty">{loading ? t('loading') : t('not_found')}</p>;
  }
  return (
    <div className="page" data-testid="siteroom">
      <Routes>
        <Route index element={<StationList stations={stations} />} />
        <Route path=":aid/*" element={<StationRoute stations={stations} />} />
      </Routes>
    </div>
  );
}

function StationList({ stations }: { stations: SiteRoomStation[] }) {
  const { t } = useTranslation('siteroom');
  return (
    <>
      <div>
        <h1 className="page-title">{t('title')}</h1>
        <p className="muted notice">{t('subtitle')}</p>
      </div>
      <div className="table-wrap">
        <table className="grid" data-testid="siteroom-stations">
          <thead>
            <tr>
              <th>{t('col.site')}</th>
              <th>{t('col.station')}</th>
              <th>{t('col.online')}</th>
            </tr>
          </thead>
          <tbody>
            {stations.map((s) => (
              <tr key={s.homeId + s.aid}>
                <td>{s.homeName}</td>
                <td>
                  <Link to={`/siteroom/${encodeURIComponent(s.aid)}`}>{s.name}</Link>
                </td>
                <td>{s.online ? <span className="badge badge-ok">{t('online')}</span> : <span className="badge">{t('offline')}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StationRoute({ stations }: { stations: SiteRoomStation[] }) {
  const { t } = useTranslation('siteroom');
  const { aid } = useParams();
  const s = stations.find((x) => x.aid === aid);
  if (!s) return <p className="card muted">{t('not_found')}</p>;
  return <StationView s={s} />;
}

interface Target {
  key: string;
  tool: string;
  dut?: string;
  label: string;
}

function StationView({ s }: { s: SiteRoomStation }) {
  const { t } = useTranslation('siteroom');
  const tabs = availableTabs(s.tools);
  const [tab, setTab] = useState(tabs[0]?.key ?? 'overview');
  const cur = tabs.find((x) => x.key === tab) ?? tabs[0];
  // 總覽回報的資源（伺服器 / NAS 分頁逐台列；有多台時 srv.health / nas.health 要帶 dut）；null = 總覽還沒查到
  const [resources, setResources] = useState<{ id: string; type: string }[] | null>(null);
  const onResult = useCallback((tool: string, run: SiteRun) => {
    const rs = (run.result as { resources?: { id: string; type: string }[] } | undefined)?.resources;
    if (tool === 'site.status' && Array.isArray(rs)) setResources(rs);
  }, []);
  const rtype = cur ? TAB_RESOURCE[cur.key] : undefined;
  const targets: Target[] = !cur
    ? []
    : rtype && resources
      ? resources.filter((r) => r.type === rtype).map((r) => ({ key: cur.tools[0] + '|' + r.id, tool: cur.tools[0], dut: r.id, label: r.id }))
      : cur.tools.map((n) => ({ key: n, tool: n, label: t(`tool.${n}`, { defaultValue: n }) }));
  const [sel, setSel] = useState('');
  const target = targets.find((x) => x.key === sel) ?? targets[0];
  return (
    <>
      <div>
        <p className="muted small">
          <Link to="/siteroom">{t('title')}</Link> › {s.homeName}
        </p>
        <h1 className="page-title">{s.name}</h1>
      </div>
      <div className="tabs" role="tablist">
        {tabs.map((x) => (
          <button key={x.key} role="tab" aria-selected={x.key === cur?.key} className={x.key === cur?.key ? 'active' : ''} onClick={() => setTab(x.key)} data-testid={`siteroom-tab-${x.key}`}>
            {t(`tab.${x.key}`)}
          </button>
        ))}
      </div>
      {targets.length > 1 && (
        <div className="tabs" role="tablist" aria-label={cur ? t(`tab.${cur.key}`) : undefined}>
          {targets.map((x) => (
            <button key={x.key} role="tab" aria-selected={x.key === target?.key} className={x.key === target?.key ? 'active' : ''} onClick={() => setSel(x.key)}>
              {x.label}
            </button>
          ))}
        </div>
      )}
      {rtype && resources && targets.length === 0 && <p className="card muted">{t('no_resources')}</p>}
      {target && <ToolResult key={s.aid + target.key} s={s} tool={target.tool} dut={target.dut} onResult={onResult} />}
    </>
  );
}

function ToolResult({ s, tool, dut, onResult }: { s: SiteRoomStation; tool: string; dut?: string; onResult?: (tool: string, run: SiteRun) => void }) {
  const { t } = useTranslation('siteroom');
  const [run, setRun] = useState<SiteRun | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  // useSiteRoom 每收到一台站的工具清單就換一個新的 station 物件：只認 aid / 家戶 / plane，不然同一個工具會重送好幾次、撞 5 秒限流
  const sRef = useRef(s);
  sRef.current = s;
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const alive = useRef({ on: true });
  useEffect(() => {
    const a = { on: true };
    alive.current = a;
    return () => {
      a.on = false; // 換分頁：舊的輪詢停掉（查結果也有每分鐘 60 次的限流）
    };
  }, []);
  const { aid, homeId, api } = s;
  const go = useCallback(async () => {
    const a = alive.current;
    setBusy(true);
    setErr(null);
    try {
      const r = await runAndWait(sRef.current, tool, { params: dut ? { dut } : undefined, onWait: setWait, cancelled: () => !a.on });
      if (!a.on) return;
      setRun(r);
      if (r.state === 'succeeded') onResultRef.current?.(tool, r);
    } catch (e) {
      if (a.on) setErr(e);
    } finally {
      if (a.on) {
        setBusy(false);
        setWait(0);
      }
    }
  }, [aid, homeId, api, tool, dut]); // eslint-disable-line react-hooks/exhaustive-deps -- 故意只認穩定的鍵（見上）
  useEffect(() => {
    void go();
  }, [go]);
  return (
    <section className="card" data-testid={`siteroom-result-${tool}`}>
      <div className="row between">
        <span className="muted small">
          {run?.ended_at ? t('updated', { at: new Date(run.ended_at).toLocaleString() }) : ''}
          {wait > 0 ? t('wait', { s: wait }) : ''}
        </span>
        <button className="btn btn-sm" onClick={() => void go()} disabled={busy} data-testid="siteroom-refresh">
          {busy ? t('running') : t('refresh')}
        </button>
      </div>
      {err != null && <p className="error">{t('error', { code: errText(err) })}</p>}
      {run && run.state !== 'succeeded' && !busy && <p className="error">{t('run_state', { state: run.state, code: run.error?.code ?? '' })}</p>}
      {run?.state === 'succeeded' && <ResultView result={run.result} />}
    </section>
  );
}

export function ResultView({ result }: { result: unknown }) {
  const blocks = resultBlocks(result);
  return (
    <>
      {blocks.map((b, i) =>
        b.kind === 'table' ? (
          <div key={i}>
            {b.title && <h3>{b.title}</h3>}
            <div className="table-wrap">
              <table className="grid">
                <thead>
                  <tr>
                    {b.columns.map((c) => (
                      <th key={c}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, j) => (
                    <tr key={j}>
                      {b.columns.map((c) => (
                        <td key={c}>{cellText(r[c])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : b.kind === 'lines' ? (
          <div key={i}>
            {b.title && <h3>{b.title}</h3>}
            <pre className="small">{b.lines.join('\n')}</pre>
          </div>
        ) : (
          <div key={i}>
            {b.title && <h3>{b.title}</h3>}
            <dl className="kv">
              {b.fields.map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{cellText(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ),
      )}
    </>
  );
}
